import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/Modal";
import { useEditEventModal } from "@circle-react/components/Modals/EditEventModal";
import { useCurrentSpaceContext } from "@circle-react/contexts/currentSpaceContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { fetchRequest } from "@circle-react/helpers/backendRequestHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Event } from "@circle-react/types/Event";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { SpaceSelect } from "@/react/components/SettingsApp/CompassCompat/SpaceSelect";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { legacyClassNames } from "@/react/helpers/cssAnchorHelpers";

import type { DuplicateEventModalProps } from ".";

const DISPLAY_EDIT_MODAL_DELAY_MILLISECONDS = 500;

interface DuplicatedEventParams {
  space_id?: number;
}

export const UIKitModal = ({
  event,
  onClose,
  show,
}: DuplicateEventModalProps) => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const {
    isLoading,
    helpers: { findById: findSpace, spacesWithEventCreationAllowed },
  } = useSpacesContext();
  const { data: space } = useCurrentSpaceContext();
  const editEventModal = useEditEventModal();
  const handleDuplicate = useMutation<Event, Error, DuplicatedEventParams>({
    mutationFn: async data => {
      const response = await fetchRequest<Event>(
        internalApi.events.duplicate({
          spaceId: event.space_id,
          slug: event.slug,
        }),
        {
          method: "PUT",
          body: JSON.stringify({
            event: {
              event_id: event.id,
              space_id: data.space_id,
            },
          }),
          forReactQuery: true,
        },
      );
      if (!response || !("id" in response)) {
        throw new Error("Failed to duplicate event");
      }
      return response as Event;
    },
    onSuccess: duplicatedEvent => {
      const eventSpace = findSpace(duplicatedEvent.space_id);
      if (!eventSpace) {
        error(t("space_not_found"));
        return;
      }
      void navigate({
        to: "/c/$spaceSlug/$postSlug",
        params: {
          spaceSlug: eventSpace.slug,
          postSlug: duplicatedEvent.slug,
        },
      });
      setTimeout(
        () => void editEventModal.show({ event: duplicatedEvent }),
        DISPLAY_EDIT_MODAL_DELAY_MILLISECONDS,
      );
      success(t("event_duplicated_successfully"));
    },
    onError: () => {
      error(t("event_duplication_failed"));
    },
  });

  if (isLoading) return null;
  const spaces = spacesWithEventCreationAllowed();

  return (
    <Modal
      overflowVisible
      title={t("duplicate_event_title", { event_title: event.name })}
      show={show}
      onClose={onClose}
    >
      <Form
        onSubmit={values => {
          handleDuplicate.mutate({ space_id: values.space_id });
        }}
        defaultValues={{
          space_id: space?.id,
        }}
      >
        <Form.Item
          name="space_id"
          hideBorder
          fullWidth
          description={t("duplicate_event_to_space")}
          rules={{
            required: t("events.create.form.space_required_error"),
          }}
        >
          <SpaceSelect
            spaces={spaces}
            name="space_id"
            variant="form-input"
            defaultValue={space?.id}
          />
        </Form.Item>
        <p
          className={classNames(
            legacyClassNames("modal__small"),
            "mb-4 text-dark",
          )}
        >
          {t("attendees_wont_be_duplicated")}
        </p>
        <Button type="submit" variant="primary">
          {t("duplicate")}
        </Button>
      </Form>
    </Modal>
  );
};
